import { z } from 'zod';

import { MESSAGE_TYPES } from './message';

// 시맨틱 버전 파싱
export function parseVersion(version: string): [number, number, number] {
  const parts = version.split('.').map(Number);
  return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
}

// 두 버전 비교 (v1 > v2: 양수, v1 < v2: 음수, 같으면 0)
export function compareVersions(v1: string, v2: string): number {
  const [major1, minor1, patch1] = parseVersion(v1);
  const [major2, minor2, patch2] = parseVersion(v2);
  if (major1 !== major2) return major1 - major2;
  if (minor1 !== minor2) return minor1 - minor2;
  return patch1 - patch2;
}

// 최소 지원 버전 이상인지 확인
export function isVersionSupported(version: string | undefined, minVersion: string): boolean {
  if (!version) return true;
  return compareVersions(version, minVersion) >= 0;
}

const VersionSchema = z
  .string()
  .regex(/^\d+\.\d+\.\d+$/)
  .optional();

// 알림창 메시지 스키마
export const AlertMessageSchema = z.object({
  type: z.literal(MESSAGE_TYPES.ALERT),
  version: VersionSchema,
  payload: z.object({ isOpen: z.boolean() }),
});

// 디바이스 토큰 응답 스키마
export const DeviceTokenResponseSchema = z.object({
  type: z.literal(MESSAGE_TYPES.DEVICE_TOKEN),
  version: VersionSchema,
  payload: z.object({ deviceToken: z.string() }),
});

// 네비게이트 메시지 스키마 (App -> Web)
export const NavigateMessageSchema = z.object({
  type: z.literal(MESSAGE_TYPES.NAVIGATE),
  version: VersionSchema,
  payload: z.object({
    target: z.enum(['mission', 'missionHistory', 'nicojini', 'diary', 'craving']),
  }),
});

// API 응답 스키마
export const ApiResponseSchema = z.object({
  resultCode: z.enum(['S', 'F']),
  resultMsg: z.string().optional(),
  data: z.unknown().optional(),
  version: VersionSchema,
});

// 본인인증 응답 스키마
export const AuthVerificationResponseSchema = z.object({
  resultCode: z.enum(['S', 'F']),
  statusCode: z.union([z.string(), z.number()]).optional(),
  data: z.unknown().optional(),
  errorMessage: z.string().optional(),
  version: VersionSchema,
});

// 언어 변경 메시지 스키마 (Web -> App, 전송 전용)
export const LanguageChangeMessageSchema = z.object({
  type: z.literal(MESSAGE_TYPES.LANGUAGE_CHANGE),
  version: VersionSchema,
  payload: z.object({ language: z.string() }),
});

// 수신 메시지 통합 스키마 (LANGUAGE_CHANGE는 전송 전용이므로 제외)
export const ReceiveMessageSchema = z.union([
  AlertMessageSchema,
  DeviceTokenResponseSchema,
  AuthVerificationResponseSchema,
  NavigateMessageSchema,
]);

// 타입 추론
export type AlertMessage = z.infer<typeof AlertMessageSchema>;
export type DeviceTokenResponse = z.infer<typeof DeviceTokenResponseSchema>;
export type ApiResponse = z.infer<typeof ApiResponseSchema>;
export type ReceiveMessage = z.infer<typeof ReceiveMessageSchema>;
export type AuthVerificationResponse = z.infer<typeof AuthVerificationResponseSchema>;
export type NavigateMessage = z.infer<typeof NavigateMessageSchema>;
export type LanguageChangeMessage = z.infer<typeof LanguageChangeMessageSchema>;

// 수신 메시지 파싱
export function parseReceiveMessage(data: unknown): ReceiveMessage | null {
  const result = ReceiveMessageSchema.safeParse(data);
  return result.success ? result.data : null;
}

// 타입 가드 함수들
export function isAlertMessage(data: unknown): data is AlertMessage {
  return AlertMessageSchema.safeParse(data).success;
}

export function isDeviceTokenResponse(data: unknown): data is DeviceTokenResponse {
  return DeviceTokenResponseSchema.safeParse(data).success;
}

export function isValidMessage(data: unknown): data is ReceiveMessage {
  return ReceiveMessageSchema.safeParse(data).success;
}

export function isApiResponse(data: unknown): data is ApiResponse {
  return ApiResponseSchema.safeParse(data).success;
}

export function isAuthVerificationResponse(data: unknown): data is AuthVerificationResponse {
  return AuthVerificationResponseSchema.safeParse(data).success;
}

export function isNavigateMessage(data: unknown): data is NavigateMessage {
  return NavigateMessageSchema.safeParse(data).success;
}
